let activeModal: HTMLDialogElement | null = null;
let activeTrigger: HTMLElement | null = null;
let lockedScrollX = 0;
let lockedScrollY = 0;
let previousBodyCssText = '';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

function getFocusableElements(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    element => element.getAttribute('aria-hidden') !== 'true' && element.getClientRects().length > 0
  );
}

function trapFocus(modal: HTMLDialogElement, event: KeyboardEvent): void {
  if (event.key !== 'Tab') return;

  const focusableElements = getFocusableElements(modal);
  const firstElement = focusableElements[0];
  const lastElement = focusableElements.at(-1);

  if (!firstElement || !lastElement) {
    event.preventDefault();
    modal.focus();
    return;
  }

  const focusIsOutsideModal =
    !(document.activeElement instanceof HTMLElement) || !modal.contains(document.activeElement);

  if (event.shiftKey && (document.activeElement === firstElement || focusIsOutsideModal)) {
    event.preventDefault();
    lastElement.focus();
  } else if (!event.shiftKey && (document.activeElement === lastElement || focusIsOutsideModal)) {
    event.preventDefault();
    firstElement.focus();
  }
}

function setPageScrollLocked(locked: boolean): void {
  if (locked && !document.documentElement.classList.contains('modal-open')) {
    lockedScrollX = window.scrollX;
    lockedScrollY = window.scrollY;
    previousBodyCssText = document.body.style.cssText;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${lockedScrollY}px`;
    document.body.style.left = `-${lockedScrollX}px`;
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }

  document.documentElement.classList.toggle('modal-open', locked);

  if (!locked) {
    document.body.style.cssText = previousBodyCssText;
    window.scrollTo(lockedScrollX, lockedScrollY);
  }
}

function closeModal(modal: HTMLDialogElement): void {
  if (!modal.open) return;

  modal.close();
}

function openModal(modal: HTMLDialogElement, trigger: HTMLElement): void {
  if (activeModal && activeModal !== modal) closeModal(activeModal);

  activeModal = modal;
  activeTrigger = trigger;
  setPageScrollLocked(true);
  modal.showModal();
}

function restoreFocus(modal: HTMLDialogElement): void {
  if (activeModal !== modal) return;

  activeModal = null;
  setPageScrollLocked(false);
  activeTrigger?.focus();
  activeTrigger = null;
}

export default function modal(root: HTMLElement): void {
  if (!(root instanceof HTMLDialogElement) || !root.id) return;

  root.querySelectorAll<HTMLElement>('[data-modal-close]').forEach(closeButton => {
    closeButton.addEventListener('click', () => closeModal(root));
  });

  root.addEventListener('click', event => {
    if (event.target === root) closeModal(root);
  });

  root.addEventListener('cancel', event => {
    event.preventDefault();
    closeModal(root);
  });

  root.addEventListener('keydown', event => trapFocus(root, event));

  root.addEventListener('close', () => restoreFocus(root));

  document.addEventListener('click', event => {
    const trigger =
      event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-modal-trigger]')
        : null;
    if (!trigger || trigger.dataset.targetModalId !== root.id) return;
    event.preventDefault();
    openModal(root, trigger);
  });
}
